import React, { memo, useLayoutEffect, useState } from "react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./SkeletonComponent.scss";

const THEMES = {
  light: { baseColor: "#ebe6ef", highlightColor: "#f7f1f8" },
  dark: { baseColor: "#2c2433", highlightColor: "#3d3348" },
};

const CELL_WIDTHS = ["72%", "58%", "80%", "45%", "64%", "70%", "52%", "76%"];

const useDarkMode = () => {
  const [isDarkMode, setIsDarkMode] = useState(() =>
    document.body.classList.contains("dark-mode")
  );

  useLayoutEffect(() => {
    const sync = () => {
      const nextDark = document.body.classList.contains("dark-mode");
      setIsDarkMode((prev) => (prev === nextDark ? prev : nextDark));
    };

    sync();
    const mo = new MutationObserver(sync);
    mo.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, []);

  return isDarkMode;
};

const TableSkeleton = ({ rows = 8, columns = 6, columnWidths }) => {
  const widths = columnWidths?.length ? columnWidths : CELL_WIDTHS;

  return (
    <div className="skeleton-table">
      <div className="skeleton-table__head">
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton
            key={`head-${index}`}
            height={12}
            width={widths[index % widths.length]}
          />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div className="skeleton-table__row" key={`row-${rowIndex}`}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton
              key={`cell-${rowIndex}-${colIndex}`}
              height={12}
              width={widths[(colIndex + rowIndex) % widths.length]}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

const CardSkeleton = ({ count = 1 }) => (
  <div className="skeleton-cards">
    {Array.from({ length: count }).map((_, index) => (
      <div className="skeleton-card" key={`card-${index}`}>
        <div className="skeleton-card__header">
          <Skeleton height={16} width="40%" />
          <Skeleton circle width={18} height={18} />
        </div>
        <Skeleton height={140} />
        <Skeleton count={2} className="mt-2" />
      </div>
    ))}
  </div>
);

const FormSkeleton = ({ rows = 5 }) => (
  <div className="skeleton-form">
    {Array.from({ length: rows }).map((_, index) => (
      <div className="skeleton-form__field" key={`field-${index}`}>
        <Skeleton height={12} width="28%" />
        <Skeleton height={36} />
      </div>
    ))}
  </div>
);

const ListSkeleton = ({ rows = 6 }) => (
  <div className="skeleton-list">
    {Array.from({ length: rows }).map((_, index) => (
      <div className="skeleton-list__item" key={`item-${index}`}>
        <Skeleton circle width={40} height={40} />
        <div className="skeleton-list__text">
          <Skeleton height={12} width="60%" />
          <Skeleton height={10} width="40%" />
        </div>
      </div>
    ))}
  </div>
);

const DashboardSkeleton = ({ count = 6 }) => (
  <div className="skeleton-dashboard">
    <div className="skeleton-dashboard__head">
      <Skeleton height={22} width={180} />
      <div className="skeleton-dashboard__actions">
        <Skeleton height={36} width={140} />
        <Skeleton height={36} width={140} />
        <Skeleton height={36} width={120} />
      </div>
    </div>
    <CardSkeleton count={count} />
  </div>
);

const SkeletonComponent = memo(
  ({
    variant = "line",
    count = 1,
    width,
    height,
    circle = false,
    rows,
    columns = 6,
    columnWidths,
    className = "",
    containerClassName = "",
    borderRadius,
    inline,
    duration,
    enableAnimation = true,
    children,
    ...rest
  }) => {
    const isDarkMode = useDarkMode();
    const theme = isDarkMode ? THEMES.dark : THEMES.light;
    const wrapperClassName = [
      "skeleton-component",
      `skeleton-component--${variant}`,
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const lineProps = {
      count,
      width,
      height,
      circle,
      borderRadius,
      inline,
      duration,
      enableAnimation,
      containerClassName,
      ...rest,
    };

    const renderVariant = () => {
      if (children) return children;

      switch (variant) {
        case "table":
          return (
            <TableSkeleton
              rows={rows ?? 8}
              columns={columns}
              columnWidths={columnWidths}
            />
          );
        case "card":
          return <CardSkeleton count={count} />;
        case "form":
          return <FormSkeleton rows={rows ?? 5} />;
        case "list":
          return <ListSkeleton rows={rows ?? 6} />;
        case "dashboard":
          return <DashboardSkeleton count={count} />;
        case "circle":
          return (
            <Skeleton
              {...lineProps}
              circle
              width={width || height || 40}
              height={height || width || 40}
            />
          );
        case "line":
        default:
          return <Skeleton {...lineProps} />;
      }
    };

    return (
      <SkeletonTheme
        baseColor={theme.baseColor}
        highlightColor={theme.highlightColor}
      >
        <div
          className={wrapperClassName}
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label="Loading"
        >
          {renderVariant()}
        </div>
      </SkeletonTheme>
    );
  }
);

const ChartSkeleton = ({
  height = 320,
  variant = "bar",
  className = "",
}) => {
  const isDarkMode = useDarkMode();
  const barHeights = [160, 220, 120, 260, 180];
  const columnPercents = [35, 28, 92, 55, 68];
  const rowPercents = [48, 32, 58, 28, 86];

  return (
    <SkeletonTheme
      baseColor={isDarkMode ? "#2b3035" : "#e9ecef"}
      highlightColor={isDarkMode ? "#3a4046" : "#f8f9fa"}
    >
      <div
        className={`common-chart-skeleton ${className}`.trim()}
        style={{ minHeight: height, height }}
        aria-busy="true"
        aria-label="Loading chart"
        role="status"
      >
        {variant === "pie" ? (
          <div className="common-chart-skeleton__pie">
            <Skeleton circle width={180} height={180} />
            <div className="common-chart-skeleton__legend">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="d-flex align-items-center gap-2 mb-2">
                  <Skeleton width={14} height={14} borderRadius={3} />
                  <Skeleton height={12} width={90} />
                </div>
              ))}
            </div>
          </div>
        ) : variant === "column" ? (
          <div className="common-chart-skeleton__graph">
            <div className="common-chart-skeleton__plot">
              <div className="common-chart-skeleton__y-axis" aria-hidden="true">
                {["100%", "75%", "50%", "25%", "0%"].map((tick) => (
                  <span key={tick}>{tick}</span>
                ))}
              </div>
              <div className="common-chart-skeleton__plot-body">
                <div className="common-chart-skeleton__grid" aria-hidden="true">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i} />
                  ))}
                </div>
                <div className="common-chart-skeleton__columns">
                  {columnPercents.map((pct, i) => (
                    <div key={i} className="common-chart-skeleton__column">
                      <div
                        className="common-chart-skeleton__column-bar"
                        style={{ height: `${pct}%` }}
                      >
                        <Skeleton height="100%" borderRadius="6px 6px 0 0" />
                      </div>
                      <Skeleton height={8} width={36} className="mt-2" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="common-chart-skeleton__legend-row">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="d-flex align-items-center gap-2">
                  <Skeleton width={12} height={12} borderRadius={2} />
                  <Skeleton height={10} width={52} />
                </div>
              ))}
            </div>
          </div>
        ) : variant === "barH" ? (
          <div className="common-chart-skeleton__graph">
            <div className="common-chart-skeleton__h-plot">
              <div className="common-chart-skeleton__h-rows">
                {rowPercents.map((pct, i) => (
                  <div key={i} className="common-chart-skeleton__h-row">
                    <Skeleton height={10} width={44} />
                    <div className="common-chart-skeleton__h-track">
                      <div
                        className="common-chart-skeleton__h-bar"
                        style={{ width: `${pct}%` }}
                      >
                        <Skeleton height={18} borderRadius={4} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="common-chart-skeleton__x-axis" aria-hidden="true">
                {["0%", "25%", "50%", "75%", "100%"].map((tick) => (
                  <span key={tick}>{tick}</span>
                ))}
              </div>
            </div>
            <div className="common-chart-skeleton__legend-row">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="d-flex align-items-center gap-2">
                  <Skeleton width={12} height={12} borderRadius={2} />
                  <Skeleton height={10} width={44} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="common-chart-skeleton__bars">
            {barHeights.map((barHeight, i) => (
              <div key={i} className="common-chart-skeleton__bar-col">
                <Skeleton height={barHeight} width="70%" borderRadius={6} />
                <Skeleton height={10} width="80%" className="mt-2" />
              </div>
            ))}
          </div>
        )}
      </div>
    </SkeletonTheme>
  );
};

export { Skeleton, SkeletonTheme, ChartSkeleton };
export default SkeletonComponent;
